/**
 * CVE IDs in a pasted list or a scanner's report (Nessus or Qualys CSV,
 * .nessus XML, a plain list), read in the browser: only the IDs go into the
 * link, never hosts, plugin output or anything else in the report. Kept free
 * of imports so the front-end bundle can use it.
 */

/** Scan reports run large (a .nessus file holds every plugin's output); the page reads them, the Worker never sees them. */
export const MAX_SCAN_BYTES = 50_000_000;

/**
 * A CVE ID anywhere in text. Word boundaries on both sides, so a longer run of
 * digits isn't cut short into a different ID. No nested repeats, so it runs in
 * linear time on any input.
 */
const CVE_IN_TEXT = /\bCVE-(\d{4})-(\d{4,7})\b/gi;

export interface CveList {
  /** Distinct IDs, uppercase, by year and then number. */
  ids: string[];
  /** Characters the IDs take up, repeats included: how much of the text is the list. */
  chars: number;
}

export function extractCveIds(text: string): CveList {
  const seen = new Map<string, [number, number]>();
  let chars = 0;
  for (const m of text.matchAll(CVE_IN_TEXT)) {
    chars += m[0].length;
    // Kept as written: CVE-2024-01234 is a typo for some ID, and guessing which could name a real, different CVE.
    const id = `CVE-${m[1]}-${m[2]}`;
    if (!seen.has(id)) seen.set(id, [Number(m[1]), Number(m[2])]);
  }
  const ids = [...seen].sort(([, a], [, b]) => a[0] - b[0] || a[1] - b[1]).map(([id]) => id);
  return { ids, chars };
}

/**
 * True when text reads as a list of CVEs rather than a description that
 * mentions one: CVE IDs make up at least half of it, spaces and separators
 * aside, or it's too long to be a description and has some.
 */
export function looksLikeCveList(text: string, maxDescription: number): boolean {
  const { ids, chars } = extractCveIds(text);
  if (ids.length === 0) return false;
  if (text.length > maxDescription) return true;
  const solid = text.replace(/[\s,;|"']+/g, '').length;
  return chars >= solid / 2;
}
